import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
} from 'react-aria-components'
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components'
import type { ReactNode } from 'react'
import { cx } from '../../cx'

export interface TextFieldProps extends Omit<AriaTextFieldProps, 'className'> {
  label?: string
  hint?: string
  /** Error message. Presence also marks the field invalid. */
  error?: string
  placeholder?: string
  /** Leading adornment, e.g. a unit or icon. */
  start?: ReactNode
  /** Trailing adornment. */
  end?: ReactNode
  className?: string
}

/**
 * Over the design project's bare <input> this adds: label/input association
 * without hand-written ids, `aria-describedby` wiring for the hint, and errors
 * announced to screen readers via FieldError rather than only shown in colour.
 *
 * Colour alone is not a sufficient error signal under WCAG 2.2 AA — the message
 * text is what carries it.
 */
export function TextField({
  label,
  hint,
  error,
  placeholder,
  start,
  end,
  className,
  ...rest
}: TextFieldProps) {
  const hasAdornment = Boolean(start || end)
  return (
    <AriaTextField
      {...rest}
      isInvalid={Boolean(error) || rest.isInvalid}
      className={cx('bui-field', error && 'bui-field-error', className)}
    >
      {label ? <Label className="bui-label">{label}</Label> : null}
      {hasAdornment ? (
        <div className="bui-inputwrap">
          {start ? <span className="bui-adorn">{start}</span> : null}
          <Input placeholder={placeholder} />
          {end ? <span className="bui-adorn">{end}</span> : null}
        </div>
      ) : (
        <Input className="bui-input" placeholder={placeholder} />
      )}
      {hint && !error ? <Text slot="description" className="bui-hint">{hint}</Text> : null}
      <FieldError className="bui-hint">{error}</FieldError>
    </AriaTextField>
  )
}
